import PageBackTitle from "../Component/PageBackTitle";
import React, { useState, useRef, useContext } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";
import API_URL from "../Config";
import Select from "react-select";
import { FiUpload } from "react-icons/fi";
import { MdAttachFile, MdDeleteOutline } from "react-icons/md";
import { IoCloseOutline } from "react-icons/io5";
import "../TableColor.css";
import useClickOutside from "../useClickOutside";
import { ThemeContext } from "../ThemeContext";
import DatePicker from "react-datepicker";
import "./manualWorkPermit.css";

function ManualWorkPermit({ isSidebarExpanded }) {
  const navigate = useNavigate();
  const { theme } = useContext(ThemeContext);

  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [severity, setSeverity] = useState("success");
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const [date, setDate] = useState(null);
  const [permitNumber, setPermitNumber] = useState("");
  const [selectedPermitType, setSelectedPermitType] = useState(null);
  const [description, setDescription] = useState("");

  const [selectedFiles, setSelectedFiles] = useState([]);
  const [isPopupOpen, setIsPopupOpen] = useState(false);

  const [errors, setErrors] = useState({});

  const fileInputRef = useRef(null);
  const containerRef = useRef(null);
  const buttonRef = useRef(null);

  useClickOutside([containerRef, buttonRef, fileInputRef], () => {
    setIsPopupOpen(false);
  });

  const today = new Date();

  const permitTypeOptions = [
    { value: "Cold Work Permit", label: "Cold Work Permit" },
    { value: "Hot Work Permit", label: "Hot Work Permit" },
    {
      value: "Confined Space Entry Permit",
      label: "Confined Space Entry Permit",
    },
  ];

  const handleDateChange = (selectedDate) => {
    setDate(selectedDate);
    setErrors((prev) => ({ ...prev, date: "" }));
  };

  const handlePermitNumberChange = (e) => {
    setPermitNumber(e.target.value);
    setErrors((prev) => ({ ...prev, permitNumber: "" }));
  };

  const handleDescriptionChange = (e) => {
    setDescription(e.target.value);
  };

  const handlePermitTypeChange = (selectedOption) => {
    setSelectedPermitType(selectedOption);
    setErrors((prev) => ({ ...prev, permitType: "" }));
  };

  const handleUploadButtonClick = () => {
    fileInputRef.current.click();
    setErrors((prev) => ({ ...prev, attachments: "" }));
  };

  const handleFileInputChange = (files) => {
    const fileArray = Array.from(files);
    setSelectedFiles(fileArray);
    setErrors((prev) => ({ ...prev, attachments: "" }));
  };

  const handleDeleteFile = (indexToDelete) => {
    const updatedFiles = selectedFiles.filter(
      (_, index) => index !== indexToDelete
    );
    setSelectedFiles(updatedFiles);

    if (updatedFiles.length === 0) {
      setIsPopupOpen(false);
    }
  };

  const handleIconClick = () => {
    setIsPopupOpen((prev) => !prev);
  };

  const handleCancelClick = () => {
    navigate("/manual-workpermit");
  };

  const handleSaveClick = async () => {
    let validationErrors = {};

    if (!date) validationErrors.date = "Date is required.";
    if (!permitNumber.trim())
      validationErrors.permitNumber = "Permit number is required.";
    if (!selectedPermitType)
      validationErrors.permitType = "Permit type is required.";
    if (selectedFiles.length === 0)
      validationErrors.attachments = "At least one attachment is required.";

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    try {
      setIsLoading(true);
      const authKey = localStorage.getItem("authKey");

      const formData = new FormData();
      // const formattedDate = new Date(date).toISOString().split("T")[0];
      const formattedDate = `${date.getFullYear()}-${String(
        date.getMonth() + 1
      ).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
      formData.append("date", formattedDate);
      formData.append("permit_type", selectedPermitType.value);
      formData.append("permit_number", permitNumber);
      formData.append("description", description);

      selectedFiles.forEach((file) => {
        formData.append("attachments", file);
      });

      const response = await axios.post(
        `${API_URL}/api/manual_work_permit`,
        formData,
        {
          headers: {
            Authorization: authKey,
            "Content-Type": "multipart/form-data",
          },
        }
      );

      if (response.status === 200) {
        setSeverity("success");
        showSnackbar("Manual Work Permit added successfully.");
        setTimeout(() => navigate("/manual-workpermit"), 1000);
      }
    } catch (error) {
      console.error("Error saving Manual Work Permit:", error);
      setSeverity("error");
      showSnackbar("Failed to save Manual Work Permit.");
    } finally {
      setIsLoading(false);
    }
  };

  const showSnackbar = (message) => {
    setSnackbarMessage(message);
    setSnackbarOpen(true);
  };

  const handleSnackbarClose = (event, reason) => {
    if (reason === "clickaway") return;
    setSnackbarOpen(false);
  };

  const handleCloseAttachment = () => {
    setIsPopupOpen(false);
  };

  return (
    <>
      <div
        className={`data-form-content-container page-content ${
          isSidebarExpanded ? "expanded" : "collapsed"
        }`}
      >
        <div className="data-form-content" ref={containerRef}>
          <div className="data-form-header">
            <div className="data-form-header-content">
              <PageBackTitle className="data-form-heading-content-left-side">New Manual Work Permit</PageBackTitle>
            </div>
          </div>

          <div className="header-separator"></div>

          <div className="data-form-body">
            <div className="action-purchase-header">
              <div className="form-group">
                <label htmlFor="date">
                  Date<span className="text-danger">*</span>
                </label>
                <div className="input-group">
                  <DatePicker
                    selected={date}
                    onChange={handleDateChange}
                    placeholderText="Select date"
                    dateFormat="dd/MM/yyyy"
                    isClearable
                    showYearDropdown
                    showMonthDropdown
                    dropdownMode="select"
                    maxDate={today}
                    className={`${
                      theme === "dark" ? "dark-theme-datepicker" : ""
                    } half-width-input`}
                  />
                </div>
                {errors.date && (
                  <div className="error-message">{errors.date}</div>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="permitNumber">
                  Permit No<span className="text-danger">*</span>
                </label>
                <div className="input-group">
                  <input
                    type="text"
                    name="permitNumber"
                    value={permitNumber}
                    onChange={handlePermitNumberChange}
                    placeholder="Enter permit number"
                  />
                </div>
                {errors.permitNumber && (
                  <span className="error-message">{errors.permitNumber}</span>
                )}
              </div>

              <div className="form-group-select">
                <label htmlFor="permitType">
                  Permit Type<span className="text-danger">*</span>
                </label>
                <Select
                  options={permitTypeOptions}
                  placeholder="Select permit type"
                  isSearchable
                  onChange={handlePermitTypeChange}
                  value={selectedPermitType}
                />
                {errors.permitType && (
                  <span className="error-message">{errors.permitType}</span>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="description">Description</label>
                {/* <div className=""> */}
                <textarea
                  name="description"
                  rows={4}
                  cols={10}
                  value={description}
                  onChange={handleDescriptionChange}
                  placeholder="Enter description"
                  className="textarea-manual-work-permit-add"
                />
                {/* </div> */}
              </div>

              <div className="form-group-attachments">
                <label htmlFor="attachments">
                  Attachments<span className="text-danger">*</span>
                </label>
                <button
                  type="button"
                  className="challan-btn"
                  onClick={handleUploadButtonClick}
                >
                  <FiUpload /> Upload File
                </button>
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={(e) => handleFileInputChange(e.target.files)}
                  style={{ display: "none" }}
                  multiple
                />

                {selectedFiles.length > 0 && (
                  <span
                    className="inquiry-file-btn"
                    onClick={handleIconClick}
                    ref={buttonRef}
                  >
                    <MdAttachFile className="inquiry-file-icon" />{" "}
                    {selectedFiles.length}
                  </span>
                )}

                {isPopupOpen && (
                  <div className="attach-popup-inward" ref={containerRef}>
                    <div className="attach-popup-contant">
                      <div className="attachment-header">
                        <div className="attachment-header-content">
                          Attachments
                        </div>
                        <button
                          className="fiile-close-btn"
                          onClick={handleCloseAttachment}
                        >
                          <IoCloseOutline className="file-close-icon" />
                        </button>
                      </div>
                      {selectedFiles.length > 0 && (
                        <div className="attach-scrroll-body">
                          {selectedFiles.map((file, index) => (
                            <div
                              className="attach-popup-contant-body"
                              key={index}
                            >
                              <div className="attach-file-container">
                                <div className="attach-file-show">
                                  <div className="text-attachment">
                                    {file.name}
                                  </div>
                                  <div className="text-attachment-file">
                                    File Size: {(file.size / 1024).toFixed(2)}{" "}
                                    KB
                                  </div>
                                </div>
                                <div
                                  className="file-delete-btn"
                                  onClick={() => handleDeleteFile(index)}
                                >
                                  <MdDeleteOutline className="file-delete-icon" />
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {errors.attachments && (
                  <span className="error-message">{errors.attachments}</span>
                )}
              </div>
            </div>

            <div className="footer">
              <div className="button-group">
                <button
                  className="data-form-save-button"
                  onClick={handleSaveClick}
                  disabled={isLoading}
                >
                  {isLoading ? "Saving..." : "Save"}
                </button>
                <button
                  className="data-form-cancel-button"
                  onClick={handleCancelClick}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        style={{ top: "75px", marginLeft: "20px" }}
      >
        <MuiAlert onClose={handleSnackbarClose} severity={severity}>
          {snackbarMessage}
        </MuiAlert>
      </Snackbar>
    </>
  );
}

export default ManualWorkPermit;
